import { useState, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudArrowUp, faCheck, faXmark, faFile, faTriangleExclamation, faCamera } from "@fortawesome/free-solid-svg-icons";
import Spinner from "@serie3/freelancesplaceapi/Assets/img/spinner.gif";
import CrudApi from "@serie3/common/api/crudApi";

const FileDropzone = ({
  uploadUrl,
  additionalData,
  edit,
  data = [],
  setName = true,
  setData,
  setShowPayModal,
}) => {
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedfiles] = useState([]);
  const [location, setLocation] = useState();
  const [isMobile, setIsMobile] = useState(false);
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setLocation(window.location.hostname);
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const allowedTypes = ["image/jpeg", "image/png", "image/gif", "text/plain", "application/pdf"];

  const assignFile = (file, status) => {
    const previewURL = allowedTypes.includes(file.type) ? URL.createObjectURL(file) : null;
    Object.assign(file, { preview: previewURL, uploadStatus: status });
  };

  const handleUpload = (file, edit) => {
    if (uploadedFiles.indexOf(file) !== -1) return null;
    const url = uploadUrl || "/documents/";

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = async () => {
      let uploadData = {
        file_string: reader.result,
        file_name: file.name,
        original_name: file.name,
      };
      if (setName === true) uploadData.name = file.name;
      if (additionalData) uploadData = { ...uploadData, ...additionalData };

      const response = (!edit || edit !== true)
        ? await CrudApi.createNew(url, uploadData, setShowPayModal)
        : await CrudApi.edit(url, uploadData);

      file.uploadStatus = response.status === 200 ? "ok" : "fail";

      if (response.status === 200) {
        if (typeof data === "object") data.push(response.data);
        if (setData) setData([...data]);
      }

      const tmpUploaded = [...uploadedFiles, file];
      setUploadedfiles(tmpUploaded);
      setFiles([...new Set([...tmpUploaded])]);
    };
    return null;
  };

  const onDrop = (acceptedFiles, fileRejections) => {
    const accepted = acceptedFiles.map((file) => { assignFile(file, "uploading"); return file; });
    const rejected = fileRejections.map(({ file }) => { assignFile(file, "toobig"); return file; });
    setFiles([...uploadedFiles, ...accepted, ...rejected]);
    accepted.forEach((file) => handleUpload(file, edit));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ maxSize: 5000000, onDrop });

  const handleFilePick = (e) => {
    const picked = Array.from(e.target.files);
    if (!picked.length) return;
    picked.forEach((file) => { assignFile(file, "uploading"); });
    setFiles((prev) => [...prev, ...picked]);
    picked.forEach((file) => handleUpload(file, edit));
    e.target.value = "";
  };

  const handleCameraCapture = (e) => {
    const captured = Array.from(e.target.files);
    if (!captured.length) return;
    captured.forEach((file) => { assignFile(file, "uploading"); });
    setFiles((prev) => [...prev, ...captured]);
    captured.forEach((file) => handleUpload(file, edit));
    e.target.value = "";
  };

  const StatusOverlay = ({ file }) => {
    if (file.uploadStatus === "uploading") return (
      <div style={{
        position: "absolute", inset: 0, background: "rgba(255,255,255,0.85)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        borderRadius: "10px",
      }}>
        <img src={Spinner} alt="uploading" style={{ width: "28px" }} />
        <span style={{ fontSize: "0.72rem", color: "#9e9e9e", marginTop: "6px" }}>Uploading…</span>
      </div>
    );
    if (file.uploadStatus === "ok") return (
      <div style={{
        position: "absolute", top: "8px", right: "8px", width: "22px", height: "22px",
        borderRadius: "50%", background: "#2e7d32",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <FontAwesomeIcon icon={faCheck} style={{ color: "#fff", fontSize: "0.65rem" }} />
      </div>
    );
    if (file.uploadStatus === "fail") return (
      <div style={{
        position: "absolute", top: "8px", right: "8px", width: "22px", height: "22px",
        borderRadius: "50%", background: "#c62828",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <FontAwesomeIcon icon={faXmark} style={{ color: "#fff", fontSize: "0.65rem" }} />
      </div>
    );
    if (file.uploadStatus === "toobig") return (
      <div style={{
        position: "absolute", inset: 0, background: "rgba(255,235,238,0.93)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        borderRadius: "10px", padding: "8px", textAlign: "center",
      }}>
        <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: "#c62828", fontSize: "1.1rem", marginBottom: "5px" }} />
        <span style={{ fontSize: "0.7rem", color: "#c62828", fontWeight: 600 }}>Too large (max 5 MB)</span>
      </div>
    );
    return null;
  };

  const borderColor = (status) => {
    if (status === "ok") return "#2e7d32";
    if (status === "fail" || status === "toobig") return "#c62828";
    return "#e0e0e0";
  };

  const FileCard = ({ file, index }) => {
    const isImage = file.type?.startsWith("image/");
    const isPdf = file.type === "application/pdf";
    return (
      <div style={{
        position: "relative", background: "#fff",
        border: `2px solid ${borderColor(file.uploadStatus)}`,
        borderRadius: "10px", overflow: "hidden",
        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
        height: "140px", display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {file.preview && isImage
          ? <img src={file.preview} alt={file.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : file.preview && isPdf
          ? <object data={file.preview} style={{ width: "100%", height: "100%" }} />
          : (
            <div style={{ textAlign: "center", padding: "12px" }}>
              <FontAwesomeIcon icon={faFile} style={{ fontSize: "1.8rem", color: "#bdbdbd", marginBottom: "6px" }} />
              <div style={{ fontSize: "0.72rem", color: "#9e9e9e", wordBreak: "break-word" }}>{file.name}</div>
            </div>
          )
        }
        <StatusOverlay file={file} />
      </div>
    );
  };

  if (location === "demo.freelancersplace.net") {
    return <div style={{ textAlign: "center", color: "#9e9e9e", padding: "24px" }}>File upload is disabled on the demo system.</div>;
  }

  /* Hidden inputs (used by both mobile and desktop) */
  const hiddenInputs = (
    <>
      <input {...getInputProps()} />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,text/plain,application/pdf"
        multiple
        onChange={handleFilePick}
        style={{ display: "none" }}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCameraCapture}
        style={{ display: "none" }}
      />
    </>
  );

  if (isMobile) {
    return (
      <div>
        {hiddenInputs}
        <div style={{ display: "flex", gap: "10px", marginBottom: files.length > 0 ? "16px" : 0 }}>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            style={{
              flex: 1, padding: "12px 0", background: "#1a73e8", color: "#fff",
              border: "none", borderRadius: "10px", fontSize: "0.92rem", fontWeight: 600,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            }}
          >
            <FontAwesomeIcon icon={faCloudArrowUp} />
            Upload File
          </button>
          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            style={{
              flex: 1, padding: "12px 0", background: "#fff", color: "#1a73e8",
              border: "2px solid #1a73e8", borderRadius: "10px", fontSize: "0.92rem", fontWeight: 600,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            }}
          >
            <FontAwesomeIcon icon={faCamera} />
            Take Photo
          </button>
        </div>
        {files.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "12px" }}>
            {files.map((file, index) => (
              <FileCard key={file.name + "_" + index} file={file} index={index} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Drop zone */}
      <div
        {...getRootProps()}
        style={{
          border: `2px dashed ${isDragActive ? "#1a73e8" : "#c5d8fb"}`,
          borderRadius: "12px",
          background: isDragActive ? "#e8f0fe" : "#f5f8ff",
          padding: "40px 24px",
          textAlign: "center",
          cursor: "pointer",
          transition: "border-color 0.2s, background 0.2s",
          marginBottom: files.length > 0 ? "20px" : 0,
        }}
      >
        {hiddenInputs}
        <div style={{
          width: "52px", height: "52px", borderRadius: "14px",
          background: isDragActive ? "#1a73e8" : "#e8f0fe",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 14px",
          transition: "background 0.2s",
        }}>
          <FontAwesomeIcon
            icon={faCloudArrowUp}
            style={{ fontSize: "1.4rem", color: isDragActive ? "#fff" : "#1a73e8" }}
          />
        </div>
        <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#212121", marginBottom: "6px" }}>
          {isDragActive ? "Drop files here" : "Drag & drop files here"}
        </div>
        <div style={{ fontSize: "0.82rem", color: "#9e9e9e", marginBottom: "16px" }}>
          Images, PDFs, text files · Max 5 MB per file
        </div>
        <div style={{ display: "inline-flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
          <div style={{ padding: "8px 22px", background: "#1a73e8", color: "#fff", borderRadius: "8px", fontSize: "0.85rem", fontWeight: 600, boxShadow: "0 2px 8px rgba(26,115,232,0.25)" }}>
            Browse Files
          </div>
          <div
            onClick={(e) => { e.stopPropagation(); cameraInputRef.current?.click(); }}
            style={{ padding: "8px 18px", background: "#fff", color: "#1a73e8", borderRadius: "8px", fontSize: "0.85rem", fontWeight: 600, border: "2px solid #1a73e8", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }}
          >
            <FontAwesomeIcon icon={faCamera} />
            Take Photo
          </div>
        </div>
      </div>

      {/* File previews */}
      {files.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "12px" }}>
          {files.map((file, index) => (
            <FileCard key={file.name + "_" + index} file={file} index={index} />
          ))}
        </div>
      )}
    </div>
  );
};

export default FileDropzone;
